import React from 'react';
import { Box, Button, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { CustomAppRemoteContainer } from './CustomAppRemoteContainer';
import { useCustomAppRetry } from './useCustomAppRetry';
import { ErrorBoundary } from '../components/ErrorBoundary';

/**
 * Panel-sized failure state for a custom app that did not load.
 *
 * A custom app is mounted in three non-page containers — a details-view
 * section, an action modal and a grid view — and none of them can borrow the
 * app-wide `GenericErrorPage`, which is written for a full viewport: it
 * overflowed the section it sat in (DP-6140), and its default control is
 * "Refresh Page", wired to `window.location.reload()`. In a modal that throws
 * away the modal and whatever the user had open, which is a worse outcome than
 * the failure it is offering to fix.
 *
 * So this says what failed in panel-sized terms and offers a retry scoped to
 * the panel. Built from the same centred-in-`h-full` shape the other empty
 * states use, so the states of one container look like siblings.
 *
 * The wording names the SECTION, not the app behind it. A custom app is an
 * implementation detail of how this section is assembled — the reader sees a
 * section, and telling them an "app could not be reached" or that something
 * "may still be deploying" describes our deployment topology rather than their
 * problem, and guesses at a cause we have not established.
 */
export function CustomAppUnavailable({
  onRetry,
}: Readonly<{ onRetry: () => void }>) {
  const { t } = useTranslation();
  return (
    <Box className="flex h-full min-h-0 flex-col items-center justify-center gap-2 overflow-hidden bg-card p-6 text-center">
      <Typography variant="h6">
        {t('dashboard.customSection.unavailable', 'Section failed to load')}
      </Typography>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('dashboard.customSection.retry', 'Try again')}
      </Button>
    </Box>
  );
}

/**
 * A custom app mounted in a panel, with the panel-sized failure state and a
 * retry scoped to it.
 *
 * Exists as a component rather than a hook call at each site because the retry
 * state belongs to one mounted app, and the grid view resolves which app it is
 * per render — a hook cannot be called from inside that callback.
 */
export function CustomAppPanel(
  props: React.ComponentProps<typeof CustomAppRemoteContainer>
) {
  const { retryKey, onRetry } = useCustomAppRetry(props.config.remoteName);
  return (
    <ErrorBoundary
      key={retryKey}
      fallback={<CustomAppUnavailable onRetry={onRetry} />}
    >
      <CustomAppRemoteContainer {...props} />
    </ErrorBoundary>
  );
}
